import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import type { HyroxSemanticColors } from 'app/design-tokens/hyrox-semantic-colors';
import type { LeadStatus } from 'app/features/leads/services/gym-finder/types';
import Typography from 'app/ui/components/atoms/typography';

type LeadStatusPillProps = {
  status: LeadStatus;
  testId?: string;
};

type ToneTokens = {
  background: keyof HyroxSemanticColors;
  border: keyof HyroxSemanticColors;
  text: keyof HyroxSemanticColors;
};

const TONE_TOKENS_BY_STATUS: Record<LeadStatus, ToneTokens> = {
  new: {
    background: 'success_500',
    border: 'success_500',
    text: 'success_600',
  },
  contacted: {
    background: 'warnning_500',
    border: 'warnning_600',
    text: 'warnning_600',
  },
  attended: {
    background: 'info_500',
    border: 'info_500',
    text: 'info_600',
  },
};

// Neutral tone used when the API sends a status we don't have an explicit mapping
// for (e.g. a new upstream lifecycle state). Keeps the pill rendering instead of
// crashing on `toneTokens.background`.
const FALLBACK_TONE_TOKENS: ToneTokens = {
  background: 'neutrals_500',
  border: 'neutrals_500',
  text: 'neutrals_600',
};

const resolveToneTokens = (status: string): ToneTokens =>
  TONE_TOKENS_BY_STATUS[status as LeadStatus] ?? FALLBACK_TONE_TOKENS;

// Turns an unknown status key (e.g. `partially_qualified`) into a readable label
// so the pill shows something meaningful when no translation exists.
const humaniseStatus = (status: string): string => {
  const words = status.trim().replace(/_+/g, ' ').replace(/\s+/g, ' ');
  return words ? `${words[0].toUpperCase()}${words.slice(1)}` : '';
};

const PillContainer = styled.span<{ $background: string; $border: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => `${spacing(1)} ${spacing(3)}`};
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  border: 0.0625rem solid ${({ $border }) => $border};
  background: ${({ $background }) => $background};
  white-space: nowrap;
`;

const PillLabel = styled(Typography)`
  font-size: 0.75rem;
  font-weight: ${({ theme: { typography } }) => typography.bold};
  line-height: 1rem;
`;

export const LeadStatusPill = ({ status, testId }: LeadStatusPillProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const toneTokens = resolveToneTokens(status);
  const label = intl.formatMessage({ id: `leads.status.${status}`, defaultMessage: humaniseStatus(status) });

  return (
    <PillContainer
      $background={`${semantic[toneTokens.background]}1f`}
      $border={semantic[toneTokens.border]}
      data-testid={testId ?? `lead-status-${status}`}
    >
      <PillLabel variant="body3" color={semantic[toneTokens.text]}>
        {label}
      </PillLabel>
    </PillContainer>
  );
};

export default LeadStatusPill;
